import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { WebSocketTokenService } from './websocket-token.service';
import { SystemUserService } from './system-user.service';

export interface WebSocketMessage {
  type: string;
  data: unknown;
  timestamp: string;
}

export interface SystemUpdate {
  entity: string;
  action: string;
  data: unknown;
}

export interface SupportUpdate {
  entity: string;
  action: string;
  data: unknown;
  // Envelope timestamp from the server message (when present) - lets
  // consumers build stable ids for the same event delivered twice.
  timestamp?: number;
}

// ---------------------------------------------------------------------------
// Support ticket websocket events
// Server message: { event_type: 'support_ticket_event', data, timestamp }.
// Only users with support_tickets.view receive these (filtered server-side,
// per tenant); 'mentioned' goes only to the mentioned user.
// ---------------------------------------------------------------------------
export type SupportTicketEventAction =
  | 'created'
  | 'updated'
  | 'assigned'
  | 'message_added'
  | 'archived'
  | 'unarchived'
  | 'deleted'
  | 'mentioned';

export type SupportTicketEventActorType = 'staff' | 'customer' | 'system';

export interface SupportTicketEventTicket {
  id: number;
  title: string;
  status: string;
  priority: string;
  assigned_to_user_id: number | null;
  portal_customer_id: number | null;
  origin: string;
  is_archived: boolean;
}

export interface SupportTicketEvent {
  action: SupportTicketEventAction;
  ticket: SupportTicketEventTicket;
  actor_user_id: number | null;
  actor_type: SupportTicketEventActorType;
  message: { id: number; is_internal: boolean } | null;
  changed_fields: string[] | null;
}

/** SupportUpdate as emitted for entity 'ticket' (support_ticket_event). */
export interface SupportTicketUpdate extends SupportUpdate {
  entity: 'ticket';
  action: SupportTicketEventAction;
  data: SupportTicketEvent;
}

// ---------------------------------------------------------------------------
// Unmatched inbound email websocket events
// Server message: { event_type: 'support_unmatched_email_event', data,
// timestamp } - an unmatched support email arrived, or was assigned to a
// customer (ticket created) / dismissed. Emitted on the support stream as
// entity 'unmatched_email'; consumers just refresh (no notification).
// ---------------------------------------------------------------------------
export type SupportUnmatchedEmailAction = 'received' | 'assigned' | 'dismissed';

export interface SupportUnmatchedEmailEvent {
  action: SupportUnmatchedEmailAction;
  unmatched_email_id: number;
  portal_tenant_id: number | null;
}

/** SupportUpdate as emitted for entity 'unmatched_email'. */
export interface SupportUnmatchedEmailUpdate extends SupportUpdate {
  entity: 'unmatched_email';
  action: SupportUnmatchedEmailAction;
  data: SupportUnmatchedEmailEvent;
}

export interface OperationsUpdate {
  entity: string;
  action: string;
  data: unknown;
}

@Injectable({
  providedIn: 'root'
})
export class WebSocketService {
  private systemSocket: WebSocket | null = null;
  private systemUpdatesSubject = new BehaviorSubject<SystemUpdate | null>(null);
  // Plain Subject (not BehaviorSubject): support events are one-shot - a
  // component subscribing later (e.g. opening the ticket list) must not
  // replay a stale event and trigger a reload/notification for it.
  private supportUpdatesSubject = new Subject<SupportUpdate>();
  private operationsUpdatesSubject = new BehaviorSubject<OperationsUpdate | null>(null);
  private connectionStatusSubject = new BehaviorSubject<boolean>(false);
  private reconnectAttempts = 0;
  private reconnectDelay = 1000;
  private maxReconnectAttempts = 5;
  private isInitialized = false; // Prevent multiple initializations

  // Public observables
  public systemUpdates$ = this.systemUpdatesSubject.asObservable();
  public supportUpdates$ = this.supportUpdatesSubject.asObservable();
  public operationsUpdates$ = this.operationsUpdatesSubject.asObservable();
  public connectionStatus$ = this.connectionStatusSubject.asObservable();

  constructor(
    private webSocketTokenService: WebSocketTokenService,
    private systemUserService: SystemUserService
  ) {
    // Only initialize WebSocket for authenticated system users with appropriate roles
    if (this.systemUserService.isSystemUser() && !this.isInitialized) {
      this.initializeWebSockets();
      this.isInitialized = true;
    }
  }

  private initializeWebSockets(): void {
    // Use the single /ws endpoint with WebSocket token
    this.connectToWebSocket();
  }

  private connectToWebSocket(): void {
    try {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${environment.apiUrl.replace(/^https?:\/\//, '')}/ws`;

      // Try to get WebSocket token first
      this.webSocketTokenService.getCurrentToken().subscribe({
        next: (token) => {
          const wsUrlWithToken = `${wsUrl}?token=${encodeURIComponent(token.websocket_token)}`;
          this.systemSocket = new WebSocket(wsUrlWithToken);
          console.log('🔐 Connecting to WebSocket with WebSocket token');
          console.log('🔑 WebSocket token expires in:', token.expires_in, 'seconds');
          this.setupWebSocketHandlers();
        },
        error: (error) => {
          console.log('⚠️  WebSocket token failed, trying JWT token fallback...');
          console.error('WebSocket token error:', error);

          // Fallback to JWT token
          this.connectWithJwtToken(wsUrl);
        }
      });
    } catch (error) {
      console.error('Failed to initialize WebSocket:', error);
      // Fallback to JWT token
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      this.connectWithJwtToken(`${protocol}//${environment.apiUrl.replace(/^https?:\/\//, '')}/ws`);
    }
  }

  private connectWithJwtToken(wsUrl: string): void {
    // Try to get authentication token from user object
    const userStr = localStorage.getItem('user');
    let token = null;

    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        token = user.access_token;
        console.log('🔐 Found authenticated user:', {
          username: user.username,
          role: user.role,
          hasToken: !!token
        });
      } catch (error) {
        console.error('Error parsing user from localStorage:', error);
      }
    } else {
      console.log('👤 No authenticated user found in localStorage');
    }

    // Create WebSocket with authentication if available
    if (token) {
      const wsUrlWithAuth = `${wsUrl}?token=${encodeURIComponent(token)}`;
      this.systemSocket = new WebSocket(wsUrlWithAuth);
      console.log('🔐 Connecting to WebSocket with JWT token (fallback)');
    } else {
      this.systemSocket = new WebSocket(wsUrl);
      console.log('👤 Connecting to WebSocket without authentication (anonymous user)');
    }

    this.setupWebSocketHandlers();
  }

  private setupWebSocketHandlers(): void {
    if (!this.systemSocket) return;

    this.systemSocket.onopen = () => {
      console.log('WebSocket connected');
      this.updateConnectionStatus();
      console.log('Backend automatically registered connection to available subscriptions');
    };

    this.systemSocket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        console.log('WebSocket message received:', message);

        // Handle different message types
        if (message.event_type === 'connected') {
          console.log('✅ Connection established with subscriptions:', message.subscriptions);
          console.log('👤 User ID:', message.user_id);

          // Check if we have audit_logs subscription
          if (message.subscriptions && message.subscriptions.includes('audit_logs')) {
            console.log(
              '🎉 SUCCESS: User has audit_logs subscription - audit log updates will work!'
            );
          } else {
            console.log('⚠️  WARNING: User does not have audit_logs subscription');
            console.log('   Only task_logs subscription available');
            console.log('   Audit log updates will NOT work for this user');
          }
        } else if (
          message.event_type === 'audit_log_created' ||
          message.event_type === 'audit_log_updated'
        ) {
          console.log('🎉 AUDIT LOG UPDATE RECEIVED:', message);
          this.systemUpdatesSubject.next({
            entity: 'audit_log',
            action: 'created',
            data: message.data || message
          } as SystemUpdate);
        } else if (
          message.event_type === 'task_log_created' ||
          message.event_type === 'task_log_updated'
        ) {
          console.log('📋 Task log update received:', message);
          this.systemUpdatesSubject.next({
            entity: 'task_execution',
            action: 'status_changed',
            data: message.data || message
          } as SystemUpdate);
        } else if (message.event_type === 'support_ticket_event') {
          const data = message.data as SupportTicketEvent | undefined;
          if (data?.action && data.ticket?.id != null) {
            this.supportUpdatesSubject.next({
              entity: 'ticket',
              action: data.action,
              data,
              timestamp: typeof message.timestamp === 'number' ? message.timestamp : undefined
            } as SupportTicketUpdate);
          }
        } else if (message.event_type === 'support_unmatched_email_event') {
          const data = message.data as SupportUnmatchedEmailEvent | undefined;
          if (data?.action) {
            this.supportUpdatesSubject.next({
              entity: 'unmatched_email',
              action: data.action,
              data,
              timestamp: typeof message.timestamp === 'number' ? message.timestamp : undefined
            } as SupportUnmatchedEmailUpdate);
          }
        } else if (message.event_type === 'pong') {
          console.log('🏓 Received pong from server');
        } else if (message.event_type === 'echo') {
          console.log('🔄 Received echo from server:', message.received);
        } else if (message.event_type === 'error') {
          console.error('❌ WebSocket error from server:', message.message);
        } else {
          console.log('📨 Other WebSocket message:', message);
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    this.systemSocket.onclose = (event) => {
      console.log('WebSocket disconnected:', event.code, event.reason);
      this.updateConnectionStatus();
      this.scheduleReconnect('websocket');
    };

    this.systemSocket.onerror = (error) => {
      console.error('WebSocket error:', error);
      this.updateConnectionStatus();
    };
  }

  private updateConnectionStatus(): void {
    const isConnected = this.systemSocket?.readyState === WebSocket.OPEN;
    this.connectionStatusSubject.next(isConnected);
  }

  private scheduleReconnect(channel: string): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('Max reconnection attempts reached, stopping reconnection');
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 30000);

    console.log(
      `Scheduling WebSocket reconnect attempt ${this.reconnectAttempts} for channel ${channel} in ${delay}ms`
    );

    setTimeout(() => {
      this.connectToWebSocket();
    }, delay);
  }

  // Public methods for sending messages
  public sendMessage(type: string, data: unknown): void {
    if (this.systemSocket && this.systemSocket.readyState === WebSocket.OPEN) {
      const message: WebSocketMessage = {
        type,
        data,
        timestamp: new Date().toISOString()
      };
      this.systemSocket.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not connected, cannot send message');
    }
  }

  // Method to manually reconnect
  public reconnect(): void {
    this.reconnectAttempts = 0;
    if (this.systemSocket) {
      this.systemSocket.close();
    }
    this.initializeWebSockets();
  }

  // Method to disconnect
  public disconnect(): void {
    if (this.systemSocket) {
      this.systemSocket.close();
      this.systemSocket = null;
    }
    this.connectionStatusSubject.next(false);
  }

  // Helper methods for specific update types
  public subscribeToSystemUpdates(): Observable<SystemUpdate | null> {
    return this.systemUpdates$;
  }

  public subscribeToSupportUpdates(): Observable<SupportUpdate> {
    return this.supportUpdates$;
  }

  /** Just the support_ticket_event stream, typed. */
  public supportTicketEvents(): Observable<SupportTicketUpdate> {
    return this.supportUpdates$.pipe(
      filter((u): u is SupportTicketUpdate => u.entity === 'ticket')
    );
  }

  /** Just the support_unmatched_email_event stream, typed. */
  public supportUnmatchedEmailEvents(): Observable<SupportUnmatchedEmailUpdate> {
    return this.supportUpdates$.pipe(
      filter((u): u is SupportUnmatchedEmailUpdate => u.entity === 'unmatched_email')
    );
  }

  public subscribeToOperationsUpdates(): Observable<OperationsUpdate | null> {
    return this.operationsUpdates$;
  }

  public isConnected(): boolean {
    return this.connectionStatusSubject.value;
  }
}
